"use strict"

import React from "react"
import memoize from "memoize-one"
import deepEqual from "deep-equal"
import ResizeSensor from "css-element-queries/src/ResizeSensor"

import {
  RunBaton,
  showGraphModal,
  hideGraphModal,
  getPlotFactories,
  getAvailableRuns,
  ensureDataRequested,
  makeObservationList,
  getCrossPlotProps,
  getRunBatonVarsPerPlotKey,
  configureXAxisPerRun,
} from "../../graphing/resources/utils"

const html_body_id = "application-page"

export class AbstractBaseDashboard extends React.Component {
  constructor(props) {
    super(props)

    this._plotFactoryCache = []

    this.showGraphModal = showGraphModal.bind(this)
    this.hideGraphModal = hideGraphModal.bind(this)
    this.ensureDataRequested = ensureDataRequested.bind(this)
    this.getPlotFactories = getPlotFactories.bind(this)

    this.resize = this.resize.bind(this)
    this.widthSensorRef = this.widthSensorRef.bind(this)

    this.forceReloadPlotData = this.forceReloadPlotData.bind(this)

    this.mountResize = this.mountResize.bind(this)
    this.unmountResize = this.unmountResize.bind(this)
  }

  widthSensorRef = (div) => {
    if (this.resizeSensor) {
      this.resizeSensor.detach(this.widthSensor)
      this.resizeSensor = null
    }
    this.widthSensor = div
    if (div) {
      this.resizeSensor = new ResizeSensor(div, this.resize)
      this.resize()
    }
  }

  resize = () => {
    if (this.props.consider_key_width) {
      // Base judgement on width of space available to plots based on assuming the key is sharing the same horizontal space
      const sensorWidth = (this.widthSensor && this.widthSensor.getBoundingClientRect().width) || 960
      this.setState({
        width: Math.max(this.props.keyWidth, sensorWidth - this.state.margins_adjustment),
      })
    } else {
      // Default resize behavior bases width on size of entire page
      const rootNode = document.getElementById(html_body_id)
      const sensorWidth =
        (this.widthSensor && this.widthSensor.getBoundingClientRect().width) ||
        rootNode.getBoundingClientRect().width - 180

      this.setState({
        width: sensorWidth,
      })
    }
  }

  /* Memoize functions to remember the last passed arguments and result.
   *  If a function is next called with the same arguments then the previous result is returned without
   *  rerunning the function unnecessarily.  Passing in deepEqual to check equality of more complex arguments. */
  getAvailableRuns = memoize(getAvailableRuns, deepEqual)
  makeObservationList = memoize(makeObservationList, deepEqual)
  configureXAxisPerRun = memoize(configureXAxisPerRun, deepEqual)

  forceReloadPlotData = (plotEntry) => {
    this.ensureDataRequested(this.props.runs, this.props.runData, [plotEntry], true)
  }

  mountResize = () => {
    this.resize()
    window.addEventListener("resize", this.resize, false)
  }

  unmountResize = () => {
    window.removeEventListener("resize", this.resize, false)
    if (this.resizeSensor) {
      this.resizeSensor.detach(this.widthSensor)
      this.resizeSensor = null
    }
  }
}

// Parent Component for all class-based Observation graph dashboards EXCEPT LiveRunNavigator
class AbstractDragDropDash extends AbstractBaseDashboard {
  constructor(props) {
    super(props)

    // Refs to row divs, allowing positions to be checked when dragging. - Not used by MultiRunView
    this.plotRowRefs = []

    this.runBatons = {}

    this.batonForRun = this.batonForRun.bind(this)

    this.plotDragStart = this.plotDragStart.bind(this)
    this.plotDragUp = this.plotDragUp.bind(this)
    this.plotDragRemoveListeners = this.plotDragRemoveListeners.bind(this)
    this.doPlotDragMove = this.doPlotDragMove.bind(this)

    this.standardDidMount = this.standardDidMount.bind(this)
    this.standardWillUnmount = this.standardWillUnmount.bind(this)
  }

  standardDidMount() {
    this.mountResize()
    this.dragObjectHolder = document.createElement("div")
    const rootNode = document.getElementById(html_body_id)
    rootNode.appendChild(this.dragObjectHolder)
  }

  standardWillUnmount() {
    this.unmountResize()
    this.plotDragRemoveListeners()
    const rootNode = document.getElementById(html_body_id)
    if (rootNode.contains(this.dragObjectHolder)) {
      rootNode.removeChild(this.dragObjectHolder)
    }
  }

  plotDragStart(index, ev) {
    const dragElementRect = ev.target.getBoundingClientRect()

    this.setState({
      dragPlotIndex: index,
      dragPlotOriginX: ev.clientX + window.scrollX,
      dragPlotOriginY: ev.clientY + window.scrollY,
      dragPlotCurrentX: ev.clientX + window.scrollX,
      dragPlotCurrentY: ev.clientY + window.scrollY,
      dragPlotOffsetX: ev.clientX - dragElementRect.x,
      dragPlotOffsetY: ev.clientY - dragElementRect.y,
    })

    const rootNode = document.getElementById(html_body_id)
    rootNode.addEventListener("mousemove", this.plotDragMove, false)
    rootNode.addEventListener("mouseup", this.plotDragUp, false)
  }

  plotDragUp(ev) {
    this.setState({
      dragPlotMoved: false,
      dragPlotIndex: undefined,
    })
    this.plotDragRemoveListeners()
  }

  plotDragRemoveListeners() {
    const rootNode = document.getElementById(html_body_id)
    rootNode.removeEventListener("mousemove", this.plotDragMove, false)
    rootNode.removeEventListener("mouseup", this.plotDragUp, false)
  }

  doPlotDragMove(ev, window, currentIndex) {
    const updateGraphs = this.state.graph_variables["updateGraphs"]

    if (currentIndex !== this.state.dragPlotIndex) {
      this.props[updateGraphs]((plots) => {
        const newPlots = [...plots]
        const moveGraph = newPlots[this.state.dragPlotIndex]
        newPlots.splice(this.state.dragPlotIndex, 1)
        newPlots.splice(currentIndex, 0, moveGraph)
        return newPlots
      })
    }

    this.setState({
      dragPlotMoved: true,
      dragPlotCurrentX: ev.clientX + window.scrollX,
      dragPlotCurrentY: ev.clientY + window.scrollY,
      dragPlotIndex: currentIndex,
    })
  }

  batonForRun(id) {
    let baton = this.runBatons[id]
    if (!baton) {
      baton = new RunBaton()
      this.runBatons[id] = baton
    }
    return baton
  }
}

// Used by all Obs Dashboards EXCEPT MultiRunView, LiveRunNavigator
// Functionality related to drag/drop of plots
// TODO - Logic for drag/drop on HomeDashboard could be more fitting to actual layout
export class VerticalPlotDashboard extends AbstractDragDropDash {
  constructor(props) {
    super(props)

    // Refs to row divs, allowing positions to be checked when dragging.
    this.plotRowRefs = []

    this.plotDragMove = this.plotDragMoveVertical.bind(this)
  }

  plotDragMoveVertical(ev) {
    ev.stopPropagation()
    ev.preventDefault()

    const graphs = this.state.graph_variables["graphs"]

    if (this.state.dragPlotMoved || Math.abs(ev.clientY + window.scrollY - this.state.dragPlotOriginY) > 3) {
      let currentIndex = 0

      for (let i = 0; i < this.props[graphs].length; ++i) {
        if (this.plotRowRefs[i]) {
          if (this.plotRowRefs[i].getBoundingClientRect().top < ev.clientY - this.state.dragPlotOffsetY) {
            currentIndex = i
          }
        } else {
          return
        }
      }

      this.doPlotDragMove(ev, window, currentIndex)
    }
  }
}

// Default resize behavior bases width on size of entire page
export class VertPlotResizeDashboard extends VerticalPlotDashboard {
  constructor(props) {
    super(props)

    this._getCrossPlotProps = getCrossPlotProps.bind(this)
    this._getRunBatonVarsPerPlotKey = getRunBatonVarsPerPlotKey.bind(this)
  }
}

// Functionality related to drag/drop of plots or runs - MultiRunView
export class MultiRunViewDashboard extends AbstractDragDropDash {
  constructor(props) {
    super(props)

    // Refs to per-runID top-level dom element (for determining positions when re-ordering)
    this.runRowRefs = {}

    this.plotDragMove = this.plotDragMove.bind(this)
    this.runDragMove = this.runDragMove.bind(this)
    this.runDragUp = this.runDragUp.bind(this)

    this._getCrossPlotProps = getCrossPlotProps.bind(this)
    this._getRunBatonVarsPerPlotKey = getRunBatonVarsPerPlotKey.bind(this)
  }

  plotDragMove(ev) {
    // Plot reorganization that is specific to the layout of the MultiRunView page
    ev.stopPropagation()
    ev.preventDefault()

    const graphs = this.state.graph_variables["graphs"]

    if (this.state.dragPlotMoved || Math.abs(ev.clientX + window.scrollX - this.state.dragPlotOriginX) > 3) {
      const relPos =
        ev.clientX - this.state.dragPlotOffsetX - this.keyHolderRef.getBoundingClientRect().x - 40
      const currentIndex = Math.max(
        0,
        Math.min(
          ((relPos * this.props[graphs].length) / this.state.width) | 0,
          this.props[graphs].length - 1,
        ),
      )

      this.doPlotDragMove(ev, window, currentIndex)
    }
  }

  runDragStart(index, ev) {
    if (ev.button === 2) return // Do nothing on right-click

    const dragElementRect = ev.target.getBoundingClientRect()

    this.setState({
      dragRunIndex: index,
      dragRunOriginX: ev.clientX + window.scrollX,
      dragRunOriginY: ev.clientY + window.scrollY,
      dragRunCurrentX: ev.clientX + window.scrollX,
      dragRunCurrentY: ev.clientY + window.scrollY,
      dragRunOffsetX: ev.clientX - dragElementRect.x,
      dragRunOffsetY: ev.clientY - dragElementRect.y,
    })

    this.state.rootNode.addEventListener("mousemove", this.runDragMove, false)
    this.state.rootNode.addEventListener("mouseup", this.runDragUp, false)
  }

  runDragRemoveListeners() {
    this.state.rootNode.removeEventListener("mousemove", this.runDragMove, false)
    this.state.rootNode.removeEventListener("mouseup", this.runDragUp, false)
  }

  runDragMove(ev) {
    ev.stopPropagation()
    ev.preventDefault()
    const oldRunList = this.props.runs

    if (this.state.dragRunMoved || Math.abs(ev.clientY + window.scrollY - this.state.dragRunOriginY) > 3) {
      const runRows = oldRunList.map((runID) => this.runRowRefs)

      const runPositions = oldRunList.map((runID) => this.runRowRefs[runID].getBoundingClientRect().y)
      let newIndex

      if (runRows.every((r) => r)) {
        newIndex = 0
        for (let i = 0; i < runPositions.length; ++i) {
          if (ev.clientY - this.state.dragRunOffsetY >= runPositions[i]) {
            newIndex = i
          }
        }

        if (newIndex !== this.state.dragRunIndex) {
          const newRunList = [...oldRunList]
          const moveRun = oldRunList[this.state.dragRunIndex]
          newRunList.splice(this.state.dragRunIndex, 1)
          newRunList.splice(newIndex, 0, moveRun)

          this.props.updateRunList(newRunList)
        }
      }

      this.setState({
        dragRunMoved: true,
        dragRunCurrentX: ev.clientX + window.scrollX,
        dragRunCurrentY: ev.clientY + window.scrollY,
        dragRunIndex: newIndex,
      })
    }
  }

  runDragUp(_) {
    this.setState({
      dragRunMoved: false,
      dragRunIndex: undefined,
    })
    this.runDragRemoveListeners()
  }
}
